import React, { useState, useCallback, useMemo, useEffect } from 'react';
import StyledTabs from '../../StyledTabs/StyledTabs';
import IDSField from '../inputs/IDSField';
import { lookupStagedFiles } from '../../../../utils/stagedFiles';
import { getRepeaterInitialStagedFiles } from '../../../../utils/formCrafter/repeaterFileUtils';
import { FIELD_TYPES } from '../../../../constants/formCrafter.constant';
import styles from './TabsField.module.css';

/**
 * TabsField - Component for rendering tabbed field groups
 *
 * @param {Object} field - Tabs field definition with tabs and tabContent
 * @param {Array} allFields - All fields in the schema (to find fields by ID)
 * @param {Object} values - Current field values
 * @param {Function} onChange - Callback when any field value changes
 * @param {Function} onBlur - Callback when any field loses focus
 * @param {Object} errors - Error messages by field ID
 * @param {Object} touched - Touched state by field ID
 * @param {boolean} disabled - Whether all fields are disabled
 * @param {Function} getExistingFiles - Function to get existing files for a field (fieldId) => files[]
 * @param {Function} onServerFileDelete - Callback when a server file is deleted (fileId)
 * @param {number} formId - Current form ID (for file scoping)
 * @param {number} subformId - Current subform ID (for file scoping)
 * @param {Function} onStagedFileUpload - Eager file upload handler
 * @param {Function} onStagedFileDelete - Eager file delete handler
 * @param {boolean} eagerUploadEnabled - Enable eager uploads
 * @param {Object} draftStagedFiles - Staged files from draft
 */
const TabsField = ({
  field,
  allFields = [],
  values = {},
  onChange,
  onBlur,
  errors = {},
  touched = {},
  disabled = false,
  getExistingFiles,
  onServerFileDelete,
  formId,
  subformId,
  onStagedFileUpload,
  onStagedFileDelete,
  eagerUploadEnabled = false,
  draftStagedFiles = {},
}) => {
  const { tabs = [], tabContent = {} } = field;
  const [activeTab, setActiveTab] = useState(0);

  const getRepeaterInitialStagedFilesCb = useCallback(
    (repeaterField) =>
      getRepeaterInitialStagedFiles(
        repeaterField,
        draftStagedFiles,
        formId,
        subformId
      ),
    [draftStagedFiles, formId, subformId]
  );

  // Clamp activeTab when tabs length shrinks to avoid out-of-bounds value (prevents
  // React #185 "Maximum update depth" with apollo-react Tabs on some devices)
  const tabCount = tabs.length;
  useEffect(() => {
    if (tabCount > 0 && activeTab >= tabCount) {
      setActiveTab(Math.min(activeTab, tabCount - 1));
    }
  }, [tabCount, activeTab]);

  const safeActiveTab = tabCount > 0 ? Math.min(activeTab, tabCount - 1) : 0;

  // Handle tab change (clamp to valid range in case library sends unexpected value)
  const handleTabChange = useCallback(
    (event, newValue) => {
      const clamped = Math.max(0, Math.min(Number(newValue), tabCount - 1));
      setActiveTab(clamped);
    },
    [tabCount]
  );

  // Get the field definition by ID from allFields
  const getFieldById = useCallback(
    (fieldId) => {
      return allFields.find((f) => f.id === fieldId);
    },
    [allFields]
  );

  // Handle field change
  const handleFieldChange = useCallback(
    (fieldId) => (newValue) => {
      onChange(fieldId, newValue);
    },
    [onChange]
  );

  // Handle field blur
  const handleFieldBlur = useCallback(
    (fieldId) => () => {
      if (onBlur) {
        onBlur(fieldId);
      }
    },
    [onBlur]
  );

  // Check if a tab has any errors
  const tabHasErrors = useMemo(() => {
    const errorsByTab = {};
    tabs.forEach((tab) => {
      const tabFields = tabContent[tab.id] || [];
      const hasError = tabFields.some((tabField) => {
        const fieldId = tabField.value || tabField.id;
        return errors[fieldId];
      });
      errorsByTab[tab.id] = hasError;
    });
    return errorsByTab;
  }, [tabs, tabContent, errors]);

  if (!tabs || tabs.length === 0) {
    return null;
  }

  return (
    <div className={styles.tabsContainer}>
      <StyledTabs
        value={safeActiveTab}
        onChange={handleTabChange}
        variant={tabs.length > 4 ? 'scrollable' : undefined}
        tabs={tabs.map((tab) => ({
          key: tab.id,
          label: tab.label,
          showStar: !disabled,
          hasError: tabHasErrors[tab.id],
        }))}
      />

      {tabs.map((tab, index) => {
        // Only render content for active tab
        if (safeActiveTab !== index) return null;

        const tabFields = tabContent[tab.id] || [];

        return (
          <div key={tab.id} role="tabpanel" className={styles.tabPanel}>
            <div className={styles.tabContent}>
              {tabFields.map((tabField) => {
                // Get the actual field definition
                const fieldDef = getFieldById(tabField.value || tabField.id);
                if (!fieldDef) return null;

                const existingFiles = getExistingFiles
                  ? getExistingFiles(fieldDef.id)
                  : [];

                /**
                 * Get initial staged files for fields inside tabs.
                 * Uses centralized lookupStagedFiles utility for consistent key handling.
                 * This ensures files uploaded to tab fields work correctly with drafts.
                 */
                // Check for repeater type (case-insensitive for compatibility)
                const isRepeaterField =
                  fieldDef.type === FIELD_TYPES.REPEATER ||
                  fieldDef.type?.toLowerCase() === 'repeater' ||
                  Array.isArray(fieldDef.fields);

                const initialStagedFiles = isRepeaterField
                  ? getRepeaterInitialStagedFilesCb(fieldDef)
                  : lookupStagedFiles(
                      draftStagedFiles,
                      formId,
                      subformId,
                      fieldDef.id
                    );

                return (
                  <IDSField
                    key={fieldDef.id}
                    field={fieldDef}
                    value={values[fieldDef.id]}
                    onChange={handleFieldChange(fieldDef.id)}
                    onBlur={handleFieldBlur(fieldDef.id)}
                    error={errors[fieldDef.id]}
                    disabled={disabled}
                    touched={!!touched[fieldDef.id]}
                    existingFiles={existingFiles}
                    onServerFileDelete={onServerFileDelete}
                    formId={formId}
                    subformId={subformId}
                    onStagedFileUpload={onStagedFileUpload}
                    onStagedFileDelete={onStagedFileDelete}
                    eagerUploadEnabled={eagerUploadEnabled}
                    initialStagedFiles={initialStagedFiles}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TabsField;
